import PropTypes from 'prop-types';
import { useMemo, useEffect, useReducer, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { isSuperadmin } from 'src/api/users';

import { supabase } from './lib';
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIAL':
      return {
        loading: false,
        user: action.payload.user,
      };
    case 'LOGIN':
    case 'REGISTER':
      return {
        ...state,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
      };
    default:
      return state;
  }
};

// ----------------------------------------------------------------------

const getUserData = async (userId) => {
  const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();

  if (error) {
    console.error('Error fetching user data:', error);
  }

  return data;
};

const createUserPayload = async (user, session) => ({
  ...user,
  role: (await isSuperadmin(user.id)) ? 'superadmin' : 'user',
  userData: (await getUserData(user.id)) || null,
  session: session && {
    access_token: session.access_token,
    expires_at: session.expires_at,
    expires_in: session.expires_in,
    refresh_token: session.refresh_token,
    token_type: session.token_type,
  },
});

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const router = useRouter();

  const initialize = useCallback(async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      const user = session?.user ? await createUserPayload(session.user, session) : null;

      dispatch({
        type: 'INITIAL',
        payload: { user },
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: { user: null },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      dispatch({ type: 'LOGIN', payload: { user: null } });
      throw error;
    }

    const user = await createUserPayload(data.user, data.session);
    dispatch({ type: 'LOGIN', payload: { user } });
  }, []);

  const register = useCallback(async (email, password, firstName, lastName) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${paths.entity.root}`,
        data: {
          display_name: `${firstName} ${lastName}`,
        },
      },
    });

    if (error) {
      throw error;
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();

    if (error) {
      throw error;
    }

    dispatch({ type: 'LOGOUT' });
    router.replace(paths.auth.supabase.login);
  }, [router]);

  const forgotPassword = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${paths.auth.supabase.newPassword}`,
    });

    if (error) {
      throw error;
    }
  }, []);

  const verifyOtp = useCallback(async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'recovery',
    });

    if (error) {
      throw error;
    }

    if (data.user && data.session) {
      const user = await createUserPayload(data.user, data.session);
      dispatch({ type: 'LOGIN', payload: { user } });
    }

    return data;
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      throw error;
    }
  }, []);

  // ----------------------------------------------------------------------

  let status = 'unauthenticated';
  if (state.loading) {
    status = 'loading';
  } else if (state.user) {
    status = 'authenticated';
  }

  const memoizedValue = useMemo(
    () => ({
      user: state.user && {
        ...state.user,
        isSuperadmin: state.user.role === 'superadmin',
        displayName: state.user.user_metadata.display_name,
      },
      method: 'supabase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      register,
      logout,
      forgotPassword,
      verifyOtp,
      updatePassword,
    }),
    [forgotPassword, login, logout, verifyOtp, updatePassword, register, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
